import { useEffect, useRef } from "react";
import { parsePath, buildPath } from "@/lib/router";
import { store, useStore } from "@/lib/store";

// Successor to useUrlSync: same three responsibilities (hydrate from the URL
// once projects are loaded, mirror selection changes into the URL, handle
// back/forward) extended to the multi-view router. Session paths still use
// replaceState; view changes push entries via store.navigate.
export function useRouteSync() {
  const state = useStore();
  const hydratedRef = useRef(false);
  const selected = state.selected;
  const view = state.view;

  // Hydrate from URL once projects are loaded.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!state.projectsLoaded) return;
    const parsed = parsePath(window.location.pathname);
    if (parsed.session) {
      const source = parsed.session.machineId ?? "local";
      store.openSession(parsed.session.slug, parsed.session.sessionId, source).catch(() => {});
    } else {
      store.setView(parsed.view);
    }
    hydratedRef.current = true;
  }, [state.projectsLoaded]);

  // Write URL when the selected session changes (after hydrate has run).
  // Only while in the console view — rewriting the URL from a stale selection
  // would clobber /projects etc.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (view.name !== "console") return;
    const want = buildPath(view, selected);
    if (window.location.pathname !== want) {
      window.history.replaceState(null, "", want);
    }
  }, [view, selected.source, selected.slug, selected.sessionId]);

  // Handle browser back/forward.
  useEffect(() => {
    function onPop() {
      const parsed = parsePath(window.location.pathname);
      if (parsed.session) {
        const source = parsed.session.machineId ?? "local";
        const current = store.getSnapshot().selected;
        if (parsed.session.sessionId !== current.sessionId || source !== current.source) {
          store.openSession(parsed.session.slug, parsed.session.sessionId, source).catch(() => {});
        } else {
          store.setView(parsed.view);
        }
        return;
      }
      // Landing on home clears the selection, matching the pre-router
      // behavior for "/". Other views keep the selection so returning to the
      // console restores the open session.
      if (parsed.view.name === "home") {
        store.selectSession(null, null, null);
      }
      store.setView(parsed.view);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}
